import React from 'react';
import { formatCurrency, formatDate } from '../../lib/utils';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';

export const LoanScheduleDialog = ({ open, onOpenChange, loan, repayments }) => {
  const items = loan
    ? repayments.filter((r) => r.loan_id === loan.id).sort((a, b) => new Date(a.date) - new Date(b.date))
    : [];
  const totalPaid = items.reduce((sum, r) => sum + (r.amount || 0), 0);
  const remaining = loan ? loan.total_repayment - totalPaid : 0;
  const progress = loan?.total_repayment ? (totalPaid / loan.total_repayment) * 100 : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Repayment Schedule</DialogTitle>
          <DialogDescription>
            {loan && `${loan.borrower_name} · ${loan.business_type}`}
          </DialogDescription>
        </DialogHeader>
        {loan && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="p-3 rounded-lg border">
                <p className="text-xs text-muted-foreground">Loan Amount</p>
                <p className="text-lg font-bold">{formatCurrency(loan.loan_amount)}</p>
              </div>
              <div className="p-3 rounded-lg border">
                <p className="text-xs text-muted-foreground">Total Repayment</p>
                <p className="text-lg font-bold">{formatCurrency(loan.total_repayment)}</p>
              </div>
              <div className="p-3 rounded-lg border bg-green-50 dark:bg-green-900/10">
                <p className="text-xs text-muted-foreground">Paid So Far</p>
                <p className="text-lg font-bold text-green-600">{formatCurrency(totalPaid)}</p>
              </div>
              <div className="p-3 rounded-lg border bg-red-50 dark:bg-red-900/10">
                <p className="text-xs text-muted-foreground">Outstanding</p>
                <p className="text-lg font-bold text-red-600">{formatCurrency(Math.max(0, remaining))}</p>
              </div>
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1">
                <span>Progress</span>
                <span>{progress.toFixed(1)}%</span>
              </div>
              <Progress value={progress} className="h-2" />
            </div>
            <div className="rounded-lg border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-12">#</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Amount</TableHead>
                    <TableHead className="text-right">Running Balance</TableHead>
                    <TableHead>Notes</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {items.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-6 text-muted-foreground">
                        No repayments yet
                      </TableCell>
                    </TableRow>
                  ) : (
                    items.map((r, idx) => {
                      const cumulative = items.slice(0, idx + 1).reduce((s, x) => s + (x.amount || 0), 0);
                      const balanceAfter = Math.max(0, loan.total_repayment - cumulative);
                      return (
                        <TableRow key={r.id} data-testid={`schedule-row-${r.id}`}>
                          <TableCell className="text-muted-foreground">{idx + 1}</TableCell>
                          <TableCell>{formatDate(r.date)}</TableCell>
                          <TableCell className="text-right font-medium text-green-600">
                            {formatCurrency(r.amount)}
                          </TableCell>
                          <TableCell className="text-right">{formatCurrency(balanceAfter)}</TableCell>
                          <TableCell className="text-sm text-muted-foreground">{r.notes || '—'}</TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </div>
            <div className="text-xs text-muted-foreground">
              Issued: {formatDate(loan.issue_date)} · Term: {loan.duration_months} months · Rate: {loan.interest_rate}%
            </div>
          </div>
        )}
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
